import type { TouchState } from "./composeInputState.js";

/** px of drag for full ±1 deflection on either axis */
const STEER_RADIUS = 72;

/**
 * Virtual on-screen gamepad rendered as fixed-position DOM elements.
 * Uses Pointer Events so a single code path handles touch, mouse, and pen.
 *
 * Left zone  → 2-axis analog joystick: left/right = steer, up = gas, down = brake
 * Right zone → discrete buttons: fire / drift / boost / mine
 */
export class TouchInput {
  readonly container: HTMLElement;

  private _throttle = 0;
  private _brake = 0;
  private _steer = 0;
  private _handbrake = false;
  private _fire = false;
  private _boost = false;
  private _dropWeapon = false;

  private cameraToggleListeners: Array<() => void> = [];

  // Joystick: one pointer owns the zone
  private steerPtr: number | null = null;
  private steerOriginX = 0;
  private steerOriginY = 0;
  private knob!: HTMLElement;
  private knobBase!: HTMLElement;

  // Buttons: map pointerId → button id so releases are attributed correctly
  private btnPtrs = new Map<number, string>();

  constructor() {
    this.container = this.buildDOM();
    document.body.appendChild(this.container);
    this.knobBase = this.container.querySelector<HTMLElement>("#ti-knob-base")!;
    this.knob = this.container.querySelector<HTMLElement>("#ti-knob")!;
    this.bindSteerZone();
    // Throttle and brake are joystick-driven — no button bindings needed
    this.bindBtn("ti-fire",      () => { this._fire = true; },        () => { this._fire = false; });
    this.bindBtn("ti-handbrake", () => { this._handbrake = true; },   () => { this._handbrake = false; });
    this.bindBtn("ti-boost",     () => { this._boost = true; },       () => { this._boost = false; });
    this.bindBtn("ti-drop",      () => { this._dropWeapon = true; },  () => { this._dropWeapon = false; });
    this.bindCamToggleBtn();
  }

  /** Register a callback fired when the on-screen camera toggle button is tapped. */
  onCameraToggle(fn: () => void): void {
    this.cameraToggleListeners.push(fn);
  }

  read(): TouchState {
    return {
      throttle: this._throttle,
      brake: this._brake,
      steer: this._steer,
      handbrake: this._handbrake,
      fire: this._fire,
      boost: this._boost,
      dropWeapon: this._dropWeapon,
    };
  }

  isActive(): boolean {
    return this.steerPtr !== null || this.btnPtrs.size > 0;
  }

  show(): void { this.container.style.display = "flex"; }
  hide(): void { this.container.style.display = "none"; }

  destroy(): void { this.container.remove(); }

  // ── DOM construction ───────────────────────────────────────────────

  private buildDOM(): HTMLElement {
    const el = document.createElement("div");
    el.id = "touch-hud";
    el.style.display = "none";
    el.innerHTML = `
      <div id="ti-steer-zone">
        <div id="ti-knob-base"><div id="ti-knob"></div></div>
        <span class="ti-zone-label">↑ GAS · ← STEER → · BRAKE ↓</span>
      </div>
      <div id="ti-action-zone">
        <div class="ti-face-row">
          <button id="ti-fire" class="ti-btn ti-fire ti-fire-main">FIRE</button>
        </div>
        <div class="ti-face-row ti-face-wide">
          <button id="ti-handbrake" class="ti-btn ti-handbrake">DRIFT</button>
          <button id="ti-boost"     class="ti-btn ti-boost">BOOST</button>
        </div>
        <div class="ti-face-row ti-face-aux">
          <button id="ti-drop" class="ti-btn ti-drop">MINE</button>
        </div>
      </div>
      <button id="ti-cam-toggle" class="ti-cam-btn">CAM</button>
      <button id="ti-hide-btn" class="ti-hide-btn">HIDE</button>
    `;
    return el;
  }

  private hideButtonBound = false;

  /** Wire the HIDE button to collapse/restore the controls mid-game. Idempotent. */
  bindHideButton(): void {
    if (this.hideButtonBound) return;
    this.hideButtonBound = true;
    const btn = this.container.querySelector<HTMLButtonElement>("#ti-hide-btn")!;
    const inner = this.container.querySelector<HTMLElement>("#ti-steer-zone")!;
    const actions = this.container.querySelector<HTMLElement>("#ti-action-zone")!;
    const cam = this.container.querySelector<HTMLElement>("#ti-cam-toggle")!;
    let hidden = false;
    btn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      hidden = !hidden;
      inner.style.visibility = hidden ? "hidden" : "";
      actions.style.visibility = hidden ? "hidden" : "";
      cam.style.visibility = hidden ? "hidden" : "";
      btn.textContent = hidden ? "SHOW" : "HIDE";
    });
  }

  // ── Steering joystick ──────────────────────────────────────────────

  private bindSteerZone(): void {
    const zone = this.container.querySelector<HTMLElement>("#ti-steer-zone")!;
    const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

    zone.addEventListener("pointerdown", (e) => {
      if (this.steerPtr !== null) return;
      e.preventDefault();
      zone.setPointerCapture(e.pointerId);
      this.steerPtr = e.pointerId;
      this.steerOriginX = e.clientX;
      this.steerOriginY = e.clientY;

      const rect = zone.getBoundingClientRect();
      this.knobBase.style.left = `${e.clientX - rect.left}px`;
      this.knobBase.style.top  = `${e.clientY - rect.top}px`;
      this.knobBase.style.opacity = "1";
      this.knob.style.transform = "translate(0,0)";
      this._steer = 0;
      this._throttle = 0;
      this._brake = 0;
    });

    zone.addEventListener("pointermove", (e) => {
      if (e.pointerId !== this.steerPtr) return;
      e.preventDefault();
      const dx = e.clientX - this.steerOriginX;
      const dy = e.clientY - this.steerOriginY;

      // Horizontal → steer (drag right = negative = turn right, matches keyboard convention)
      this._steer = clamp(-dx / STEER_RADIUS, -1, 1);
      // Vertical → gas (up = negative dy = throttle) / brake (down = positive dy)
      const vert = clamp(dy / STEER_RADIUS, -1, 1);
      this._throttle = vert < 0 ? -vert : 0;
      this._brake    = vert > 0 ?  vert : 0;

      const vx = clamp(dx, -STEER_RADIUS, STEER_RADIUS);
      const vy = clamp(dy, -STEER_RADIUS, STEER_RADIUS);
      this.knob.style.transform = `translate(${vx}px,${vy}px)`;
    });

    const release = (e: PointerEvent): void => {
      if (e.pointerId !== this.steerPtr) return;
      this.steerPtr = null;
      this._steer = 0;
      this._throttle = 0;
      this._brake = 0;
      this.knobBase.style.opacity = "0";
      this.knob.style.transform = "translate(0,0)";
    };

    zone.addEventListener("pointerup",     release);
    zone.addEventListener("pointercancel", release);
  }

  // ── Camera toggle button ───────────────────────────────────────────

  private bindCamToggleBtn(): void {
    const btn = this.container.querySelector<HTMLElement>("#ti-cam-toggle")!;
    btn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      for (const fn of this.cameraToggleListeners) fn();
    });
  }

  /** Update the label on the CAM button to reflect current mode. */
  updateCamLabel(mode: "thirdPerson" | "firstPerson"): void {
    const btn = this.container.querySelector<HTMLElement>("#ti-cam-toggle");
    if (btn) btn.textContent = mode === "firstPerson" ? "3P" : "1P";
  }

  // ── Action buttons ─────────────────────────────────────────────────

  private bindBtn(id: string, onDown: () => void, onUp: () => void): void {
    const btn = this.container.querySelector<HTMLElement>(`#${id}`)!;

    btn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      btn.setPointerCapture(e.pointerId);
      this.btnPtrs.set(e.pointerId, id);
      btn.classList.add("ti-btn--active");
      onDown();
    });

    const release = (e: PointerEvent): void => {
      if (!this.btnPtrs.has(e.pointerId)) return;
      this.btnPtrs.delete(e.pointerId);
      // Only release the action if no other pointer is still holding this button
      if (![...this.btnPtrs.values()].includes(id)) {
        btn.classList.remove("ti-btn--active");
        onUp();
      }
    };

    btn.addEventListener("pointerup",     release);
    btn.addEventListener("pointercancel", release);
  }
}

/** Returns true if the primary pointing device supports touch. */
export function isTouchDevice(): boolean {
  return navigator.maxTouchPoints > 0;
}
